import {
    Button,
    Form,
    Select,
    Table,
    Space,
    Tooltip,
    Divider,
    Popconfirm,
    message,
    Input,
} from 'antd';
import { EditTwoTone, DeleteTwoTone } from '@ant-design/icons';
import React, { useState } from 'react';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import {
    DefaultPaginationConfig,
    DEFAULT_PAGE_SIZE,
} from '../../constants/pagination';
import {
    deleteUsuario,
    USUARIOS_ENDPOINT,
    getAllUsuarios,
} from '../../services/api/usuario';
import {
    DELETE_CONFIRMATION_MESSAGE,
    GENERIC_ERROR_MESSAGE,
    GENERIC_SUCCESS_MESSAGE,
} from '../../constants/feedback';
import { ESTADO_ENDPOINT, getAllEstados } from '../../services/api/enumeracion';
import {
    ESTADO_ACTIVO_DISPLAY,
    ESTADO_NO_ACTIVO_DISPLAY,
} from '../../constants/estado';
import UsuarioItem from './usuario-item';

const { Option } = Select;
const { Column } = Table;

const itemLayout = {
    labelCol: { span: 24 },
};

const initialFilters = {
    usuario: '',
    nombre: '',
    idEstado: '',
    sortField: 'nombre',
    sortOrder: 'ascend',
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
};

const UsuarioList = () => {
    const [filters, setFilters] = useState({ ...initialFilters });
    const [selectedFilters, setSelectedFilters] = useState({
        ...initialFilters,
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(0);

    const { data: estados } = useSWRImmutable(ESTADO_ENDPOINT, () =>
        getAllEstados()
    );

    const {
        data: usuarios,
        isLoading,
        mutate,
    } = useSWR([USUARIOS_ENDPOINT, filters], ([_, filters]) =>
        getAllUsuarios(filters)
    );

    const [form] = Form.useForm();

    const handleTableChange = (pagination, _, sorter) => {
        const params = {
            ...filters,
            pageNumber: Number(pagination.current),
            pageSize: Number(pagination.pageSize),
            sortField: sorter.columnKey,
            sortOrder: sorter.order,
        };

        setSelectedFilters({ ...params });
        setFilters({ ...params });
    };

    const getActionColumn = (usuario) => {
        return (
            <Space size={0}>
                <Tooltip title='Editar'>
                    <EditTwoTone
                        onClick={() => handleEditClick(usuario.idUsuario)}
                    />
                </Tooltip>

                <Divider type='vertical' />

                <Popconfirm
                    title={DELETE_CONFIRMATION_MESSAGE}
                    onConfirm={() => handleConfirmDelete(usuario.idUsuario)}
                >
                    <Tooltip title='Eliminar' className='cursor-pointer'>
                        <DeleteTwoTone />
                    </Tooltip>
                </Popconfirm>
            </Space>
        );
    };

    const handleShowSizeChange = (current, pageSize) => {
        setSelectedFilters({
            ...filters,
            pageSize: pageSize,
        });
    };

    const handleConfirmDelete = async (id) => {
        try {
            await deleteUsuario(id);

            message.success(GENERIC_SUCCESS_MESSAGE);
            mutate();
        } catch (error) {
            if (error.response.status === 409) {
                message.error(error.response.data.Message);
            } else {
                message.error(GENERIC_ERROR_MESSAGE);
            }
        }
    };

    const handleReset = async () => {
        form.resetFields();

        setSelectedFilters({ ...initialFilters });
        setFilters({ ...initialFilters });
    };

    const handleFinish = async (values) => {
        const params = {
            ...selectedFilters,
            usuario: values.usuario,
            nombre: values.nombre,
            idEstado: values.idEstado ?? null,
            pageNumber: 1,
        };

        setFilters(params);
    };

    const handleEditClick = (id) => {
        setSelectedId(id);
        setIsModalOpen(true);
    };

    const handleNewClick = () => {
        setIsModalOpen(true);
    };

    const handleSaveModal = () => {
        mutate();
        setSelectedId(0);
        setIsModalOpen(false);
    };

    const handleCancelModal = () => {
        setSelectedId(0);
        setIsModalOpen(false);
    };

    return (
        <>
            <div className='row pt-3'>
                <div className='col-lg-12'>
                    <ul className='breadcrumb shadow-sm rounded-2'>
                        <li>
                            <h6>Administración de usuarios</h6>
                        </li>
                    </ul>
                </div>

                <div className='col-lg-12'>
                    <div className='card rounded-4 shadow'>
                        <h6 className='txt-color-primary pt-3 border-bottom mx-3'>
                            Filtros
                        </h6>

                        <div className='card-body'>
                            <Form form={form} onFinish={handleFinish}>
                                <div className='row pl-2 pr-2'>
                                    <div className='col-lg-3 pt-2 pb-3'>
                                        <Form.Item
                                            name='usuario'
                                            label='Username'
                                            {...itemLayout}
                                            rules={[
                                                {
                                                    whitespace: true,
                                                },
                                            ]}
                                        >
                                            <Input maxLength={50} allowClear />
                                        </Form.Item>
                                    </div>

                                    <div className='col-lg-3 pt-2 pb-3'>
                                        <Form.Item
                                            name='nombre'
                                            label='Nombre'
                                            {...itemLayout}
                                            rules={[
                                                {
                                                    whitespace: true,
                                                },
                                            ]}
                                        >
                                            <Input maxLength={50} allowClear />
                                        </Form.Item>
                                    </div>

                                    <div className='col-lg-3 pt-2 pb-3'>
                                        <Form.Item
                                            name='idEstado'
                                            label='Estado'
                                            {...itemLayout}
                                        >
                                            <Select
                                                className='w-100'
                                                allowClear
                                            >
                                                {estados?.data.map((estado) => (
                                                    <Option
                                                        key={estado.id}
                                                        value={estado.id}
                                                    >
                                                        {estado.nombre}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div className='col-lg-12 text-end'>
                                        <Form.Item className='mb-0'>
                                            <Space>
                                                <Button
                                                    type='primary'
                                                    htmlType='submit'
                                                >
                                                    Filtrar
                                                </Button>
                                                <Button onClick={handleReset}>
                                                    Limpiar
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </div>
                                </div>
                            </Form>
                        </div>
                    </div>
                </div>
            </div>

            <div className='row pt-4 pb-5'>
                <div className='col-lg-12'>
                    <div className='card rounded-4 shadow'>
                        <h6 className='txt-color-primary pt-3 border-bottom mx-3'>
                            Usuarios
                        </h6>

                        <div className='card-body'>
                            <div className='row pl-2 pr-2'>
                                <Table
                                    rowKey={(record) => record.idUsuario}
                                    dataSource={usuarios?.data.items}
                                    pagination={{
                                        ...DefaultPaginationConfig,
                                        total: usuarios?.data.totalCount,
                                        onShowSizeChange: handleShowSizeChange,
                                    }}
                                    onChange={handleTableChange}
                                    loading={isLoading}
                                >
                                    <Column
                                        title='Username'
                                        dataIndex='usuario'
                                        key='usuario'
                                        sorter
                                        width='20%'
                                    />
                                    <Column
                                        title='Nombre'
                                        dataIndex='nombre'
                                        key='nombre'
                                        sorter
                                        width='30%'
                                    />
                                    <Column
                                        title='Correo'
                                        dataIndex='correo'
                                        key='correo'
                                        sorter
                                        width='20%'
                                    />
                                    <Column
                                        title='Estado'
                                        dataIndex='activo'
                                        key='activo'
                                        sorter
                                        width='20%'
                                        render={(estado) =>
                                            estado
                                                ? ESTADO_ACTIVO_DISPLAY
                                                : ESTADO_NO_ACTIVO_DISPLAY
                                        }
                                    />
                                    <Column
                                        title='Acción'
                                        render={(usuario) =>
                                            getActionColumn(usuario)
                                        }
                                    />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='col-lg-12 text-end mt-3'>
                    <Button type='primary' onClick={handleNewClick}>
                        Nuevo usuario
                    </Button>
                </div>
            </div>

            {isModalOpen && (
                <UsuarioItem
                    isModalOpen={isModalOpen}
                    idUsuario={selectedId}
                    onSave={handleSaveModal}
                    onCancel={handleCancelModal}
                />
            )}
        </>
    );
};

export default UsuarioList;
